import type { JSX } from "react";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, RefreshCw } from "lucide-react";
import { api, type Security, type SecurityPrice } from "../api.js";
import { Modal } from "../components/Modal.js";
import { Field, Select, TextInput } from "../components/Form.js";
import { PageHeader } from "../components/PageHeader.js";
import { PageActions } from "../components/PageActions.js";
import { Money } from "../components/Money.js";
import { Button } from "../components/ui/button.js";
import { csvMoney, downloadCsv } from "../lib/csv.js";
import { DataTable, uniq, type Column } from "../components/data-table.js";

// Prices are stored at IBKR's 4-dp minor scale (×10_000). The Money formatter
// speaks 2-dp minor units, so rescale by /100 for display, and ×10_000 on input.
const toDisplayMinor = (fourDp: number): number => Math.round(fourDp / 100);
const majorToFourDp = (major: number): number => Math.round(major * 10_000);
const fourDpToMajor = (fourDp: number): string => (fourDp / 10_000).toString();

export function StocksPrices(): JSX.Element {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<SecurityPrice | null>(null);
  const [showForm, setShowForm] = useState(false);

  const secsQ = useQuery({ queryKey: ["securities"], queryFn: () => api.securities({ limit: 1000 }) });
  const pricesQ = useQuery({ queryKey: ["security-prices"], queryFn: () => api.securityPrices({ limit: 1000 }) });

  const secById = useMemo(() => {
    const m = new Map<string, Security>();
    for (const s of secsQ.data?.items ?? []) m.set(s.id, s);
    return m;
  }, [secsQ.data]);

  const rows = pricesQ.data?.items ?? [];
  const symbolOf = (p: SecurityPrice): string => secById.get(p.security_id)?.symbol ?? p.security_id.slice(0, 8);

  const collect = useMutation({
    mutationFn: () => api.collectPrices(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["security-prices"] }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.remove(`/security-prices/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["security-prices"] }),
  });

  const exportCsv = (): void => {
    downloadCsv(
      "security-prices.csv",
      ["Date", "Symbol", "Currency", "Close", "Source"],
      rows.map((p) => [p.price_date, symbolOf(p), p.currency, csvMoney(toDisplayMinor(p.close_minor)), p.source]),
    );
  };

  const columns: Column<SecurityPrice>[] = [
    {
      key: "date",
      label: "Date",
      sort: (a, b) => a.price_date.localeCompare(b.price_date),
      cell: (p) => p.price_date,
    },
    {
      key: "symbol",
      label: "Symbol",
      sort: (a, b) => symbolOf(a).localeCompare(symbolOf(b)),
      filter: { kind: "search", accessor: (p) => symbolOf(p), placeholder: "Symbol…" },
      cell: (p) => <span className="font-medium">{symbolOf(p)}</span>,
    },
    {
      key: "close",
      label: "Close",
      align: "right",
      sort: (a, b) => a.close_minor - b.close_minor,
      cell: (p) => <Money amountMinor={toDisplayMinor(p.close_minor)} native={p.currency} />,
    },
    {
      key: "source",
      label: "Source",
      sort: (a, b) => a.source.localeCompare(b.source),
      filter: { kind: "select", accessor: (p) => p.source, options: (rs) => uniq(rs.map((p) => p.source)) },
      cell: (p) => p.source,
    },
    {
      key: "actions",
      label: "",
      cell: (p) => (
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => { setEditing(p); setShowForm(true); }}>Edit</Button>
          <Button variant="outline" size="sm" onClick={() => { if (confirm(`Delete ${symbolOf(p)} ${p.price_date}?`)) remove.mutate(p.id); }}>Del</Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader title="Prices">
        <div className="flex items-center gap-2">
          <Button onClick={() => { setEditing(null); setShowForm(true); }}>+ Add price</Button>
          <PageActions
            updatedAt={rows[0]?.observed_at ?? null}
            actions={[
              { label: collect.isPending ? "Collecting…" : "Collect now", icon: RefreshCw, onClick: () => { collect.mutate(); } },
              { label: "Export CSV", icon: Download, onClick: exportCsv },
            ]}
          />
        </div>
      </PageHeader>

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(p) => p.id}
        defaultSort="date"
        loading={pricesQ.isLoading}
        emptyText="No prices yet — run “Collect now” or add one."
      />

      <Modal open={showForm} onClose={() => { setShowForm(false); }} title={editing ? "Edit price" : "New price"}>
        <PriceForm
          initial={editing}
          securities={secsQ.data?.items ?? []}
          onDone={() => { setShowForm(false); void qc.invalidateQueries({ queryKey: ["security-prices"] }); }}
        />
      </Modal>
    </>
  );
}

function PriceForm({
  initial,
  securities,
  onDone,
}: {
  initial: SecurityPrice | null;
  securities: Security[];
  onDone: () => void;
}): JSX.Element {
  const [securityId, setSecurityId] = useState(initial?.security_id ?? securities[0]?.id ?? "");
  const [priceDate, setPriceDate] = useState(initial?.price_date ?? new Date().toISOString().slice(0, 10));
  const [currency, setCurrency] = useState(initial?.currency ?? securities[0]?.currency ?? "USD");
  const [closeMajor, setCloseMajor] = useState(initial ? fourDpToMajor(initial.close_minor) : "");
  const [source, setSource] = useState(initial?.source ?? "manual");
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        security_id: securityId,
        price_date: priceDate,
        currency,
        close_minor: majorToFourDp(Number(closeMajor)),
        source,
      };
      return initial ? api.update(`/security-prices/${initial.id}`, body) : api.create("/security-prices", body);
    },
    onSuccess: onDone,
    onError: (e: Error) => { setError(e.message); },
  });

  return (
    <form onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
      <Field label="Security *">
        <Select value={securityId} onChange={(e) => {
          setSecurityId(e.target.value);
          const s = securities.find((x) => x.id === e.target.value);
          if (s) setCurrency(s.currency);
        }}>
          {securities.map((s) => <option key={s.id} value={s.id}>{s.symbol}{s.name ? ` — ${s.name}` : ""}</option>)}
        </Select>
      </Field>
      <Field label="Date *"><TextInput type="date" required value={priceDate} onChange={(e) => { setPriceDate(e.target.value); }} /></Field>
      <Field label="Close price *"><TextInput required inputMode="decimal" value={closeMajor} onChange={(e) => { setCloseMajor(e.target.value); }} placeholder="123.45" /></Field>
      <Field label="Currency *"><TextInput required value={currency} onChange={(e) => { setCurrency(e.target.value.toUpperCase()); }} placeholder="USD" /></Field>
      <Field label="Source *"><TextInput required value={source} onChange={(e) => { setSource(e.target.value); }} placeholder="manual" /></Field>
      {error && <p className="text-bad mb-3 text-sm">{error}</p>}
      <Button type="submit" disabled={save.isPending}>{save.isPending ? "Saving…" : "Save"}</Button>
    </form>
  );
}
